import { createContext, useContext, useState, ReactNode } from 'react'

export interface Currency {
  code: string
  symbol: string
  name: string
  exchangeRate: number // Rate relative to USD
}

export const supportedCurrencies: Currency[] = [
  { code: 'USD', symbol: '$', name: 'US Dollar', exchangeRate: 1.00 },
  { code: 'EUR', symbol: '€', name: 'Euro', exchangeRate: 0.92 },
  { code: 'GBP', symbol: '£', name: 'British Pound', exchangeRate: 0.79 },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar', exchangeRate: 1.35 },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar', exchangeRate: 1.52 }
]

interface CurrencyContextType {
  selectedCurrency: Currency
  setSelectedCurrency: (currency: Currency) => void
  convertPrice: (priceInUSD: number) => number
  formatPrice: (priceInUSD: number) => string
  formatPriceInCurrency: (amount: number, currency: Currency) => string
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined)

interface CurrencyProviderProps {
  children: ReactNode
}

export const CurrencyProvider = ({ children }: CurrencyProviderProps) => {
  const [selectedCurrency, setSelectedCurrencyState] = useState<Currency>(() => {
    // Get currency from localStorage or default to USD
    const stored = localStorage.getItem('selectedCurrency')
    return supportedCurrencies.find(c => c.code === stored) || supportedCurrencies[0]
  })

  const setSelectedCurrency = (currency: Currency) => {
    setSelectedCurrencyState(currency)
    localStorage.setItem('selectedCurrency', currency.code)
  }

  const convertPrice = (priceInCents: number): number => {
    // Convert cents to dollars, apply exchange rate, then back to cents
    const priceInDollars = priceInCents / 100
    const convertedDollars = priceInDollars * selectedCurrency.exchangeRate
    return Math.round(convertedDollars * 100)
  }

  const formatPrice = (priceInCents: number): string => {
    const convertedPriceCents = convertPrice(priceInCents)
    const convertedPriceDollars = convertedPriceCents / 100
    return formatPriceInCurrency(convertedPriceDollars, selectedCurrency)
  }

  const formatPriceInCurrency = (amount: number, currency: Currency): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.code,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  const value: CurrencyContextType = {
    selectedCurrency,
    setSelectedCurrency,
    convertPrice,
    formatPrice,
    formatPriceInCurrency
  }

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  )
}

export const useCurrency = (): CurrencyContextType => {
  const context = useContext(CurrencyContext)
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider')
  }
  return context
} 